import { useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { API_BASE, INDUSTRY_VOICES_THEME } from '../../config';
import { db } from '../../firebase';
import { stripMarkdown } from '../../utils/stripMarkdown';

function looksStructuredMarkdown(value) {
  const s = String(value || '');
  return /\n{2,}/.test(s) || /^#{1,6}\s+/m.test(s) || /^\s*[-*+]\s+/m.test(s);
}

function isIndustryVoicesPost(post) {
  const category = String(post?.category || '').trim();
  if (category === INDUSTRY_VOICES_THEME) return true;
  const themes = Array.isArray(post?.themes) ? post.themes : [];
  return themes.includes(INDUSTRY_VOICES_THEME);
}

function buildExcerptFromBody(body) {
  const plain = stripMarkdown(body);
  if (!plain) return '';
  return plain.length > 220 ? `${plain.slice(0, 220).trim()}…` : plain;
}

function countWords(text) {
  return String(text || '')
    .split(/\s+/)
    .map((w) => w.trim())
    .filter(Boolean).length;
}

function AdminBulkRewrite() {
  const [posts, setPosts] = useState([]);
  const [slotReviews, setSlotReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const [includeNews, setIncludeNews] = useState(true);
  const [includeIndustryVoices, setIncludeIndustryVoices] = useState(true);
  const [includeSlotReviews, setIncludeSlotReviews] = useState(true);
  const [onlyUnformatted, setOnlyUnformatted] = useState(true);
  const [rewriteExcerpt, setRewriteExcerpt] = useState(false);
  const [maxItems, setMaxItems] = useState(20);

  const [selected, setSelected] = useState(() => new Set());
  const [status, setStatus] = useState('');
  const [running, setRunning] = useState(false);
  const stopRef = useRef(false);

  useEffect(() => {
    const postsQ = query(collection(db, 'posts'), orderBy('publishedAt', 'desc'), limit(300));
    const slotsQ = query(collection(db, 'slotReviews'), orderBy('createdAt', 'desc'), limit(300));

    let gotPosts = false;
    let gotSlots = false;

    const maybeDone = () => {
      if (gotPosts && gotSlots) setLoading(false);
    };

    const unsubPosts = onSnapshot(
      postsQ,
      (snap) => {
        setPosts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        gotPosts = true;
        maybeDone();
      },
      () => {
        setPosts([]);
        gotPosts = true;
        maybeDone();
      }
    );

    const unsubSlots = onSnapshot(
      slotsQ,
      (snap) => {
        setSlotReviews(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        gotSlots = true;
        maybeDone();
      },
      () => {
        setSlotReviews([]);
        gotSlots = true;
        maybeDone();
      }
    );

    return () => {
      unsubPosts();
      unsubSlots();
    };
  }, []);

  const candidates = useMemo(() => {
    const out = [];

    if (includeNews || includeIndustryVoices) {
      for (const p of posts) {
        const isVoices = isIndustryVoicesPost(p);
        if (isVoices && !includeIndustryVoices) continue;
        if (!isVoices && !includeNews) continue;

        const body = String(p.body || '').trim();
        if (body.length < 40) continue;
        if (onlyUnformatted && looksStructuredMarkdown(body)) continue;

        out.push({
          key: `post:${p.id}`,
          type: 'post',
          kind: isVoices ? 'industry-voices' : 'news',
          id: p.id,
          title: p.title || p.slug || 'Untitled',
          category: p.category || 'Market Stats',
          region: p.region || 'Global',
          excerpt: p.excerpt || '',
          body: p.body || '',
        });
      }
    }

    if (includeSlotReviews) {
      for (const s of slotReviews) {
        const body = String(s.body || '').trim();
        if (body.length < 40) continue;
        if (onlyUnformatted && looksStructuredMarkdown(body)) continue;
        out.push({
          key: `slot:${s.id}`,
          type: 'slotReview',
          id: s.id,
          title: s.title || s.id || 'Slot review',
          provider: s.provider || '',
          body: s.body || '',
        });
      }
    }

    // Newest first in each collection already, but keep deterministic.
    return out.slice(0, Math.max(0, Number(maxItems) || 0));
  }, [
    includeIndustryVoices,
    includeNews,
    includeSlotReviews,
    maxItems,
    onlyUnformatted,
    posts,
    slotReviews,
  ]);

  useEffect(() => {
    // Reset selection when the candidate set changes (avoid rewriting unintended items).
    setSelected(new Set(candidates.map((c) => c.key)));
  }, [candidates]);

  const selectedList = useMemo(() => candidates.filter((c) => selected.has(c.key)), [candidates, selected]);

  const toggleAll = (checked) => {
    if (checked) setSelected(new Set(candidates.map((c) => c.key)));
    else setSelected(new Set());
  };

  const toggleOne = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const rewriteOne = async (item) => {
    if (!API_BASE) {
      throw new Error('API base URL is not configured. Set REACT_APP_API_BASE_URL.');
    }

    if (item.type === 'post') {
      const resp = await fetch(`${API_BASE}/api/ai/rewrite-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: item.title,
          excerpt: item.excerpt || undefined,
          body: item.body,
          category: item.category,
          region: item.region,
          kind: item.kind,
        }),
      });
      if (!resp.ok) throw new Error('Rewrite API returned non-200 status');
      const data = await resp.json();
      const nextBody = String(data.body || '').trim();
      if (!nextBody) throw new Error('Rewrite returned empty body');

      const words = countWords(nextBody);
      const readMinutes = Math.max(2, Math.round(words / 200));
      const nextExcerpt = rewriteExcerpt
        ? String(data.suggestedExcerpt || '').trim() || buildExcerptFromBody(nextBody)
        : String(item.excerpt || '').trim() || buildExcerptFromBody(nextBody);

      await updateDoc(doc(db, 'posts', item.id), {
        aiRewriteBackup: {
          body: String(item.body || ''),
          excerpt: String(item.excerpt || ''),
          kind: item.kind,
          modelUsed: String(data?.meta?.modelUsed || ''),
          rewrittenAt: serverTimestamp(),
        },
        body: nextBody,
        excerpt: nextExcerpt,
        readMinutes,
        updatedAt: serverTimestamp(),
      });
      return;
    }

    if (item.type === 'slotReview') {
      const resp = await fetch(`${API_BASE}/api/ai/slot-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: item.body,
          slotName: item.title,
          provider: item.provider || undefined,
        }),
      });
      if (!resp.ok) throw new Error('Slot review API returned non-200 status');
      const data = await resp.json();
      const nextBody = String(data.content || '').trim();
      if (!nextBody) throw new Error('Rewrite returned empty body');

      await updateDoc(doc(db, 'slotReviews', item.id), {
        aiRewriteBackup: {
          body: String(item.body || ''),
          modelUsed: String(data?.meta?.modelUsed || ''),
          rewrittenAt: serverTimestamp(),
        },
        body: nextBody,
        updatedAt: serverTimestamp(),
      });
      return;
    }

    throw new Error(`Unknown item type: ${item.type}`);
  };

  const runBulk = async () => {
    if (!selectedList.length) return;
    const ok = window.confirm(
      `Rewrite ${selectedList.length} items?\n\nThis will overwrite body (and excerpt optionally) but will store a backup in aiRewriteBackup.`
    );
    if (!ok) return;

    stopRef.current = false;
    setRunning(true);
    setStatus('Starting rewrite…');

    let done = 0;
    let failed = 0;

    try {
      for (const item of selectedList) {
        if (stopRef.current) break;
        setStatus(`Rewriting ${done + 1}/${selectedList.length}: ${item.title}`);
        try {
          // Sequential to avoid rate limits + to keep Firestore write order stable.
          // eslint-disable-next-line no-await-in-loop
          await rewriteOne(item);
          done += 1;
        } catch (err) {
          failed += 1;
          // eslint-disable-next-line no-console
          console.error('Bulk rewrite failed for', item, err);
        }
      }
    } finally {
      setRunning(false);
      setStatus(`Done. Rewritten ${done}, failed ${failed}${stopRef.current ? ' (stopped early).' : '.'}`);
    }
  };

  return (
    <div className="admin-tab">
      <section className="content-card" aria-label="Bulk rewrite">
        <div className="content-card-header">
          <div>
            <div className="section-title">Maintenance</div>
            <h2 className="section-subtitle">Bulk rewrite / format content</h2>
          </div>
          <div className="secondary-text">
            {loading ? 'Loading…' : `${candidates.length} candidates (showing up to ${maxItems})`}
          </div>
        </div>

        <p className="secondary-text" style={{ marginTop: 4 }}>
          Finds posts/reviews that look like a single unformatted block and rewrites them into structured Markdown.
          It saves a backup in <strong>aiRewriteBackup</strong> before overwriting.
        </p>

        <div className="mini-list admin-form" style={{ marginTop: 14 }}>
          <div className="mini-item">
            <label className="mini-label">
              <input
                type="checkbox"
                checked={includeNews}
                onChange={(e) => setIncludeNews(e.target.checked)}
                disabled={running}
              />{' '}
              News posts
            </label>
          </div>
          <div className="mini-item">
            <label className="mini-label">
              <input
                type="checkbox"
                checked={includeIndustryVoices}
                onChange={(e) => setIncludeIndustryVoices(e.target.checked)}
                disabled={running}
              />{' '}
              Industry Voices
            </label>
          </div>
          <div className="mini-item">
            <label className="mini-label">
              <input
                type="checkbox"
                checked={includeSlotReviews}
                onChange={(e) => setIncludeSlotReviews(e.target.checked)}
                disabled={running}
              />{' '}
              Slot reviews
            </label>
          </div>

          <div className="mini-item">
            <label className="mini-label">
              <input
                type="checkbox"
                checked={onlyUnformatted}
                onChange={(e) => setOnlyUnformatted(e.target.checked)}
                disabled={running}
              />{' '}
              Only unformatted (recommended)
            </label>
          </div>

          <div className="mini-item">
            <label className="mini-label">
              <input
                type="checkbox"
                checked={rewriteExcerpt}
                onChange={(e) => setRewriteExcerpt(e.target.checked)}
                disabled={running}
              />{' '}
              Rewrite excerpt (optional)
            </label>
          </div>

          <div className="mini-item">
            <label className="mini-label" htmlFor="maxItems">
              Max items
            </label>
            <input
              id="maxItems"
              className="text-input"
              type="number"
              min={0}
              max={300}
              value={maxItems}
              disabled={running}
              onChange={(e) => setMaxItems(Number(e.target.value))}
            />
          </div>
        </div>

        <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="cta-button"
            onClick={runBulk}
            disabled={running || loading || !selectedList.length}
            title="Rewrite selected items sequentially"
          >
            <span>{running ? '…' : '✓'}</span> {running ? 'Rewriting…' : `Rewrite selected (${selectedList.length})`}
          </button>
          <button
            type="button"
            className="header-outline-button"
            onClick={() => {
              stopRef.current = true;
              setStatus('Stopping after current item…');
            }}
            disabled={!running}
          >
            Stop
          </button>
          <button
            type="button"
            className="header-outline-button"
            onClick={() => toggleAll(true)}
            disabled={running || loading || !candidates.length}
          >
            Select all
          </button>
          <button
            type="button"
            className="header-outline-button"
            onClick={() => toggleAll(false)}
            disabled={running || loading || !candidates.length}
          >
            Clear
          </button>
        </div>

        {status && (
          <p className="secondary-text" style={{ marginTop: 10 }}>
            {status}
          </p>
        )}

        <div className="admin-list" style={{ marginTop: 14 }}>
          {!loading && !candidates.length && (
            <p className="secondary-text">No candidates found with the current filters.</p>
          )}

          {candidates.map((c) => (
            <div key={c.key} className="admin-row">
              <div className="admin-row-main">
                <div className="admin-row-meta">
                  <div className="admin-row-title">{c.title}</div>
                  <div className="secondary-text">
                    {c.type === 'post'
                      ? `${c.kind === 'industry-voices' ? 'Industry Voices' : 'News'} · ${c.category} · ${c.region}`
                      : `Slot review${c.provider ? ` · ${c.provider}` : ''}`}
                  </div>
                </div>
              </div>
              <div className="admin-row-actions">
                <label className="secondary-text" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={selected.has(c.key)}
                    onChange={() => toggleOne(c.key)}
                    disabled={running}
                  />
                  select
                </label>
                <button
                  type="button"
                  className="header-outline-button"
                  disabled={running}
                  onClick={async () => {
                    try {
                      setStatus(`Rewriting: ${c.title}`);
                      await rewriteOne(c);
                      setStatus(`Rewritten: ${c.title}`);
                    } catch (err) {
                      // eslint-disable-next-line no-console
                      console.error('Rewrite failed', err);
                      setStatus(`Rewrite failed: ${c.title}`);
                    }
                  }}
                >
                  Rewrite now
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default AdminBulkRewrite;


